import { Security } from '../../../proto/generated/security'
import { Snapshot } from '../../../reducer/security.reducer'
import { RootState } from '../../../store'
import { calculateSnapshotDetailsForExpression } from '../../../util/expression'
import { toLocaleStringIn } from '../../../util/general'
import { calculateSnapshotDetails, getSecurityKey } from '../../../util/security'
import { green, red } from '../../../assets/colors'
import { LiveMarketTextSelector } from '../../../components/common/LiveMarketText'

const displaySelectorCache = new Map<string, LiveMarketTextSelector>()
const colorSelectorCache = new Map<string, LiveMarketTextSelector>()

const getCachedSelector = (
    cache: Map<string, LiveMarketTextSelector>,
    cacheKey: string,
    factory: () => LiveMarketTextSelector
): LiveMarketTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

export type LtpViewLiveConfig = {
    displayKey: string
    expressionKey: string
    expressionMode: boolean
    isSimulator: boolean
    simulatorUnderlyingKey: string
    security: Security | undefined
    expressionSecurities: Security[]
    narrowWidth: boolean
}

const getEffectiveSnapshot = (state: RootState, config: LtpViewLiveConfig): Snapshot | undefined => {
    if (!config.displayKey) return undefined
    const securitySnapshot = state.securityStore.snapshots[config.displayKey]
    if (!securitySnapshot || config.expressionMode) return securitySnapshot
    if (!config.isSimulator) return securitySnapshot
    const optionChainSnapShot = state.optionsStore.underlyingPrice[config.simulatorUnderlyingKey]
    if (!optionChainSnapShot) return securitySnapshot
    return {
        ...securitySnapshot,
        lastTradedPrice: optionChainSnapShot.underlyingCurrentPrice ?? 0,
        previousClose:
            optionChainSnapShot.underlyingLastDayClosingPrice ??
            optionChainSnapShot.underlyingCurrentPrice ??
            0
    }
}

const computeExpressionSnapshots = (state: RootState, securities: Security[]): Record<string, Snapshot> | null => {
    if (!securities.length) return null
    const all = state.securityStore.snapshots
    const result: Record<string, Snapshot> = {}
    for (const security of securities) {
        const key = getSecurityKey(security.exchange, security.code)
        if (all[key]) result[key] = all[key]
    }
    return Object.keys(result).length ? result : null
}

const computeStats = (state: RootState, config: LtpViewLiveConfig) => {
    if (config.expressionMode) {
        if (!config.expressionSecurities.length || !config.expressionKey) return null
        const expressionSnapshots = computeExpressionSnapshots(state, config.expressionSecurities)
        if (!expressionSnapshots) return null
        return calculateSnapshotDetailsForExpression(config.expressionKey, config.expressionSecurities, expressionSnapshots)
    }
    const snapshot = getEffectiveSnapshot(state, config)
    if (!snapshot || !config.security) return null
    return calculateSnapshotDetails(config.security, snapshot)
}

export const shouldShowLtpViewChange = (state: RootState, config: LtpViewLiveConfig): boolean => {
    const stats = computeStats(state, config)
    if (!stats) return false
    if (config.expressionMode) return true
    const snapshot = getEffectiveSnapshot(state, config)
    if (!snapshot || snapshot.previousClose === 0) return false
    return stats.ltp > 0
}

export const hasLtpViewLiveData = (state: RootState, config: LtpViewLiveConfig): boolean => {
    if (!config.expressionMode && !config.displayKey) return false
    if (config.expressionMode) {
        return config.expressionSecurities.length > 0 && !!computeStats(state, config)
    }
    return !!config.security && !!getEffectiveSnapshot(state, config) && !!computeStats(state, config)
}

export type LtpViewLiveSelectors = {
    ltp: LiveMarketTextSelector
    change: LiveMarketTextSelector
    changeColor: LiveMarketTextSelector
}

export const buildLtpViewLiveSelectors = (config: LtpViewLiveConfig): LtpViewLiveSelectors | null => {
    if (config.expressionMode) {
        if (!config.expressionKey || !config.expressionSecurities.length) return null
        const exprKey = config.expressionSecurities.map((s) => getSecurityKey(s.exchange, s.code)).join(',')
        const prefix = `ltpView:expr:${config.expressionKey}:${exprKey}:${config.narrowWidth}`

        const ltp = getCachedSelector(displaySelectorCache, `${prefix}:ltp`, () => (state) => {
            const stats = computeStats(state, config)
            return stats ? toLocaleStringIn(stats.ltp) : ''
        })

        const change = getCachedSelector(displaySelectorCache, `${prefix}:chg`, () => (state) => {
            if (!shouldShowLtpViewChange(state, config)) return ''
            const stats = computeStats(state, config)
            if (!stats) return ''
            if (config.narrowWidth) {
                return `${stats.changePercentage > 0 ? '+' : ''}${stats.changePercentage.toFixed(2)}%`
            }
            return `${stats.ltpChange > 0 ? '+' : ''}${stats.ltpChange.toFixed(2)} (${stats.changePercentage > 0 ? '+' : ''}${stats.changePercentage.toFixed(2)}%)`
        })

        const changeColor = getCachedSelector(colorSelectorCache, `${prefix}:color`, () => (state) => {
            if (!shouldShowLtpViewChange(state, config)) return 'inherit'
            const stats = computeStats(state, config)
            if (!stats || stats.ltpChange === 0) return 'inherit'
            return stats.ltpChange > 0 ? green : red
        })

        return { ltp, change, changeColor }
    }

    if (!config.displayKey || !config.security) return null
    const priceMultiplier = config.security.priceMultiplier || 100
    const prefix = `ltpView:${config.displayKey}:${config.isSimulator}:${config.simulatorUnderlyingKey}:${config.narrowWidth}:${priceMultiplier}`

    const ltp = getCachedSelector(displaySelectorCache, `${prefix}:ltp`, () => (state) => {
        const stats = computeStats(state, config)
        return stats ? toLocaleStringIn(stats.ltp) : ''
    })

    const change = getCachedSelector(displaySelectorCache, `${prefix}:chg`, () => (state) => {
        if (!shouldShowLtpViewChange(state, config)) return ''
        const stats = computeStats(state, config)
        if (!stats) return ''
        if (config.narrowWidth) {
            return `${stats.changePercentage > 0 ? '+' : ''}${stats.changePercentage.toFixed(2)}%`
        }
        return `${stats.ltpChange > 0 ? '+' : ''}${stats.ltpChange.toFixed(2)} (${stats.changePercentage > 0 ? '+' : ''}${stats.changePercentage.toFixed(2)}%)`
    })

    const changeColor = getCachedSelector(colorSelectorCache, `${prefix}:color`, () => (state) => {
        if (!shouldShowLtpViewChange(state, config)) return 'inherit'
        const stats = computeStats(state, config)
        if (!stats || stats.ltpChange === 0) return 'inherit'
        return stats.ltpChange > 0 ? green : red
    })

    return { ltp, change, changeColor }
}
